import React, { Suspense, lazy, useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
import some from 'lodash/some';
import _get from 'lodash/get';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWindows as fabFaWindows,
  faApple as fabFaApple,
  faLinux as fabFaLinux,
} from '@fortawesome/free-brands-svg-icons';

import { countryCode } from 'Helpers/Geo/data';
import BundleCouponPrice from 'Features/Bundle/components/Commerce/BundleCouponPrice';
import ProductAddToCartBtn from 'Components/ProductAddToCartBtn/ProductAddToCartBtn';
import BundlePrice from 'Components/Price/BundlePrice';
import PaymentCards from 'Components/PaymentCards/PaymentCards';
import CouponSaving from 'Components/Coupon/CouponSaving';
import DonationTotal from 'Features/Bundle/components/PayWhatYouWant/DonationTotal';
import Drm from 'Components/Utility/Drm';
import GiveawayRequired from 'Components/Giveaway/GiveawayRequired';
import GiveawaySoldOut from 'Components/Giveaway/GiveawaySoldOut';
import MysteryKeySelectionButtons from 'Features/Bundle/components/Mystery/MysteryKeySelectionButtons';
import Price from 'Components/Utility/Price/Price';
import ProductRequirements from 'Helpers/Product/ProductRequirements';
import RegionLocked from 'Components/Regional/RegionLocked';
import StarDealCountdown from 'Components/StarDeal/StarDealCountdown';
import TieredBundleSelection from 'Features/Bundle/components/Commerce/TieredBundleSelection';
import WishlistButton from 'Components/Wishlist/WishlistButton';
import SelectedTierContext from 'Features/Bundle/context/selected-tier-context';

const PayWhatYouWantButton = lazy(() => import(/* webpackChunkName: "PayWhatYouWant" */ 'Components/PayWhatYouWant/PayWhatYouWantButton'));

function BundleCommerceDetails({
  couponIncluded,
  displayTypes,
  product,
  subscribed,
  totalGameCount,
  voucher,
  deliveredGameCount,
  preSelectedPnm,
  flashDeal,
}) {
  const allEqual = arr => arr.every(v => v === arr[0]);
  const { selectedTier, inCartTier, selectTier } = useContext(SelectedTierContext);

  const {
    slug,
    bundles,
    price,
    currentPrice,
    fullPrice,
    hideDiscount,
    presale,
    current_discount: currentDiscount,
    drm,
    platforms,
    giveaway,
    giveaway_newsletter: giveawayNewsletter,
    sold_out: soldOut,
    template_type: templateType,
    discount_percent_override: discountPercentOverride,
    pay_what_you_want: payWhatYouWant,
    regions_included: includedRegions,
    regions_excluded: excludedRegions,
  } = product;

  let validDrm = [];
  if (drm) {
    validDrm = Object.keys(drm).filter(p => drm[p]);
  }

  const validPlatforms = Object.keys(platforms).filter(p => platforms[p]);

  let LockedInRegion = false;
  if (excludedRegions.length >= 1 && some(excludedRegions, { code: countryCode })) {
    LockedInRegion = true;
  }
  if (includedRegions.length >= 1 && !some(includedRegions, { code: countryCode })) {
    LockedInRegion = true;
  }

  const isCharity = _get(product, 'charity.active');
  let validCoupon = ProductRequirements.validCoupon(product, voucher);
  const totalSaving = ProductRequirements.totalSaving(product, validCoupon);

  if (totalSaving.isMonetary) validCoupon = false;

  const isStarDeal = ProductRequirements.isStarDeal(product);
  const keysAvailablePercentage = ProductRequirements.starDealKeyAvailablePercentage(product);

  return (
    <div className="details-container">
      <div className="w-100">
        {flashDeal &&
          <div className="BundlePage__label BundlePage__label--flashdeal">
            <div className="BundlePage__label__content">
              <div className="BundlePage__label__content__text">
                <FormattedMessage id="bundle-commerce-detail.label.flash-deal" defaultMessage="Flash Deal" />
              </div>
              <div className="BundlePage__label__content__slash" />
            </div>
            <div className="BundlePage__label__triangle" />
          </div>
        }
        {(!isEmpty(validDrm[0]) || !isEmpty(validPlatforms)) &&
          <div className="icons-wishlist-container">
            <div className="icons-container">
              {!isEmpty(validDrm[0]) &&
                <Drm drmType={validDrm[0]} />
              }
              {
                product.template_type !== 'standard_mystery' &&
                product.template_type !== 'tiered_mystery' &&
                <div className="platforms-container">
                  {includes(validPlatforms, 'windows') &&
                    <span>
                      <FontAwesomeIcon icon={fabFaWindows} />
                    </span>
                  }
                  {includes(validPlatforms, 'mac') &&
                    <span>
                      <FontAwesomeIcon icon={fabFaApple} />
                    </span>
                  }
                  {includes(validPlatforms, 'linux') &&
                    <span>
                      <FontAwesomeIcon icon={fabFaLinux} />
                    </span>
                  }
                </div>
              }
              {(templateType === 'standard_mystery' || templateType === 'tiered_mystery') &&
                <FontAwesomeIcon icon={fabFaWindows} />
              }
            </div>
          </div>
        }

        <div>
          {product.template_type !== 'tiered_mystery' && !validCoupon &&
            <BundlePrice
              bundles={bundles}
              price={currentPrice || price}
              fullPrice={fullPrice}
              hideDiscount={hideDiscount}
              bundleTemplate={product.template_type}
              giveaway={giveaway}
              discountPercentOverride={discountPercentOverride}
              presale={presale}
              payWhatYouWant={payWhatYouWant}
              validCoupon={validCoupon}
              totalGameCount={totalGameCount}
            />
          }

          {validCoupon && product.template_type !== 'tiered_mystery' && !payWhatYouWant && bundles.length < 2 &&
            <React.Fragment>
              <BundleCouponPrice
                price={currentPrice}
                fullPrice={fullPrice}
                couponDiscountPercent={(_get(validCoupon, 'percent', 0) / 100)}
                hideDiscount={hideDiscount}
                totalSavingPercent={totalSaving.percent}
                mystery={product.mystery}
              />

              <div className="mb-4">
                <CouponSaving
                  hasExtraSaving={totalSaving.hasExtraSaving}
                  savingPrice={totalSaving.price}
                  coupon={validCoupon}
                  extraSavingPrice={totalSaving.extraSavingPrice}
                  isMonetary={totalSaving.isMonetary}
                />
              </div>

            </React.Fragment>
          }

          {templateType === 'tiered_mystery' && (!soldOut || (soldOut && window.location.pathname.includes('/preview/'))) &&
            <MysteryKeySelectionButtons
              bundles={bundles}
              selectTier={selectTier}
            />
          }

          {bundles.length > 1 && templateType !== 'tiered_mystery' && !payWhatYouWant &&
            <TieredBundleSelection
              bundles={bundles}
              selectTier={selectTier}
              giveaway={giveaway}
              bundleType={templateType}
            />
          }
        </div>

        {soldOut && !window.location.pathname.includes('/preview/') &&
          <div className="product-soldout">
            <FormattedMessage id="bundle-commerce-detail.bundle-now-ended" defaultMessage="Sorry - This bundle has now ended." />
          </div>
        }

        {giveaway && giveawayNewsletter && !subscribed && !soldOut &&
          <GiveawayRequired />
        }
        {giveaway && giveawayNewsletter && soldOut &&
          <GiveawaySoldOut subscribed={subscribed} />
        }

        {!presale && payWhatYouWant &&
          <div className="button-wishlist-container">
            {!soldOut && !LockedInRegion &&
              <Suspense fallback={<div />}>
                <PayWhatYouWantButton product={product} />
              </Suspense>
            }
          </div>
        }

        {!presale && !payWhatYouWant &&
          <div className="button-wishlist-container">
            {!soldOut && !LockedInRegion &&
              <ProductAddToCartBtn
                oldStyle
                product={product}
                preSelectedPnm={preSelectedPnm}
                inCartTier={inCartTier}
                selectedTier={selectedTier}
              />
            }

            {!payWhatYouWant && !preSelectedPnm &&
              <WishlistButton
                bundleButton
                slug={slug}
                productId={product._id}
                product={product}
              />
            }
          </div>
        }

        {isStarDeal && !soldOut &&
          <StarDealCountdown
            keysAvailablePercentage={keysAvailablePercentage}
            soldOut={soldOut}
            currentDiscount={currentDiscount}
          />
        }

        {product.template_type === 'book' &&
          <div className="ebooks-worth-value">
            <FormattedMessage
              id="bundle-commerce-detail.book-value"
              defaultMessage="Get {deliveredGameCount} {displayType} worth {price}"
              values={{
                displayType: allEqual(displayTypes) ? 'eBooks' : 'products',
                price: <Price price={bundles[bundles.length - 1].incrementalFullPrice} />,
                deliveredGameCount: couponIncluded ? deliveredGameCount - 1 : deliveredGameCount,
              }}
            />
          </div>
        }

        <div>
          <RegionLocked included={includedRegions} excluded={excludedRegions} />
        </div>

        {isCharity &&
          <DonationTotal charity={product.charity} soldOut={soldOut} />
        }

        <div className="d-none d-md-block mt-3">
          <PaymentCards />
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = ({
  auth: { email_newsletter: emailNewsletter },
  voucher: { voucher },
}) => ({
  subscribed: emailNewsletter,
  voucher,
});

export default connect(mapStateToProps, null)(BundleCommerceDetails);

BundleCommerceDetails.propTypes = {
  couponIncluded: PropTypes.bool,
  displayTypes: PropTypes.arrayOf(PropTypes.string),
  product: PropTypes.shape({}),
  subscribed: PropTypes.bool,
  totalGameCount: PropTypes.number,
  deliveredGameCount: PropTypes.number,
  voucher: PropTypes.shape({}),
  preSelectedPnm: PropTypes.bool,
  flashDeal: PropTypes.bool,
};

BundleCommerceDetails.defaultProps = {
  couponIncluded: false,
  displayTypes: [],
  product: {},
  subscribed: false,
  totalGameCount: 0,
  deliveredGameCount: 0,
  voucher: {},
  preSelectedPnm: false,
  flashDeal: false,
};
